import React, { useEffect, useMemo, useState } from "react";
import { Globe, Search as SearchIcon, ArrowRight, MapPin, ExternalLink, Sparkles, Loader2, ClipboardCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";

const REGIONS = ["All", "Asia", "Europe", "North America", "South America", "Oceania", "Middle East", "Africa"];

function VisaList({ onOpen }) {
  const [countries, setCountries] = useState([]);
  const [region, setRegion] = useState("All");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/visa/countries").then((r) => setCountries(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    let items = countries;
    if (region !== "All") items = items.filter((c) => c.region === region);
    if (q.trim()) {
      const ql = q.toLowerCase();
      items = items.filter((c) => c.name.toLowerCase().includes(ql) || c.code.toLowerCase().includes(ql));
    }
    return items;
  }, [countries, region, q]);

  return (
    <>
      <div className="rounded-3xl bg-white border border-stone-200 p-4 md:p-5 mb-5">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-[220px] rounded-full border border-stone-200 pl-3">
            <SearchIcon size={14} className="text-stone-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search country — 'Japan', 'JP'…"
              className="flex-1 py-2 text-sm bg-transparent outline-none"
              data-testid="visa-search-input"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {REGIONS.map((r) => (
              <button
                key={r}
                onClick={() => setRegion(r)}
                className={`text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${
                  region === r ? "bg-[#2D5A27] text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"
                }`}
                data-testid={`visa-region-${r.replace(/\s+/g, "-")}`}
              >
                {r}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="visa-grid">
        {filtered.map((c) => (
          <button
            key={c.code}
            onClick={() => onOpen(c.code)}
            className="text-left rounded-2xl bg-white border border-stone-200 hover:border-[#2D5A27] hover:-translate-y-0.5 transition-all p-4"
            data-testid={`visa-country-${c.code}`}
          >
            <div className="flex items-center justify-between">
              <div className="text-3xl">{c.flag}</div>
              <div className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">{c.region}</div>
            </div>
            <div className="heading font-black text-stone-900 mt-2">{c.name}</div>
            <div className="text-xs text-stone-500 mt-1">
              From ₹{c.from_fee_inr.toLocaleString("en-IN")} · {c.visa_type_count} visa type{c.visa_type_count === 1 ? "" : "s"}
            </div>
          </button>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-sm text-stone-500 py-10">No countries match your search.</div>
        )}
      </div>
    </>
  );
}

function VisaDetail({ code, onBack }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [applying, setApplying] = useState(false);
  const [form, setForm] = useState({ visa_type: "", travel_month: "", passport_country: "IN", notes: "" });
  const [applications, setApplications] = useState([]);
  const [checklist, setChecklist] = useState(null);
  const [checklistBusy, setChecklistBusy] = useState(false);

  useEffect(() => {
    api.get(`/visa/${code}`).then((r) => {
      setData(r.data);
      setForm((f) => ({ ...f, visa_type: r.data.visa_types[0]?.name || "" }));
    }).catch(() => toast.error("Country not supported yet"));
    if (user) {
      api.get("/visa/my/applications").then((r) => setApplications(r.data.filter(a => a.country_code === code))).catch(() => {});
    }
  }, [code, user]);

  const submit = async () => {
    if (!user) {
      toast.error("Please sign in to record an application.");
      return;
    }
    setApplying(true);
    try {
      const r = await api.post(`/visa/${code}/apply`, form);
      toast.success("Application logged — opening the official portal…");
      const a = await api.get("/visa/my/applications");
      setApplications(a.data.filter((x) => x.country_code === code));
      window.open(r.data.application.portal, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the application");
    } finally {
      setApplying(false);
    }
  };

  const buildChecklist = async () => {
    if (!user) { toast.error("Sign in — this is a paid AI feature (₹1)"); return; }
    if (!form.visa_type) { toast.error("Choose a visa type first"); return; }
    setChecklistBusy(true);
    try {
      const r = await api.post("/visa/checklist", {
        country_code: code,
        visa_type: form.visa_type,
        passport_country: form.passport_country || "IN",
        purpose: form.notes,
        travel_month: form.travel_month,
      });
      setChecklist(r.data);
      toast.success("Checklist ready · ₹1 debited");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checklist failed");
    } finally { setChecklistBusy(false); }
  };

  if (!data) return <div className="text-center text-stone-500 py-16">Loading…</div>;

  const selected = data.visa_types.find((v) => v.name === form.visa_type) || data.visa_types[0];

  return (
    <div data-testid={`visa-detail-${code}`}>
      <button onClick={onBack} className="text-sm text-[#2D5A27] font-bold mb-4 hover:underline" data-testid="visa-back">← All countries</button>
      <div className="rounded-3xl bg-white border border-stone-200 p-6">
        <div className="flex items-center gap-4">
          <div className="text-6xl">{data.flag}</div>
          <div>
            <h2 className="heading text-3xl font-extrabold text-stone-900">{data.name}</h2>
            <div className="text-xs text-stone-500 uppercase tracking-widest font-bold flex items-center gap-1">
              <MapPin size={11} /> {data.region}
            </div>
          </div>
        </div>

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-xs uppercase tracking-widest font-black text-stone-500">Visa type</label>
            <select
              value={form.visa_type}
              onChange={(e) => setForm({ ...form, visa_type: e.target.value })}
              className="mt-2 w-full px-4 py-2 rounded-full border border-stone-200 text-sm outline-none focus:border-[#2D5A27]"
              data-testid="visa-type-select"
            >
              {data.visa_types.map((v) => (
                <option key={v.name} value={v.name}>{v.name}</option>
              ))}
            </select>
            {selected && (
              <div className="mt-2 text-xs text-stone-500">
                Fee: <b className="text-stone-800">₹{selected.fee_inr.toLocaleString("en-IN")}</b> ·
                Processing: <b className="text-stone-800">{selected.processing_days} days</b>
              </div>
            )}
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-black text-stone-500">Travel month</label>
            <input
              type="month"
              value={form.travel_month}
              onChange={(e) => setForm({ ...form, travel_month: e.target.value })}
              className="mt-2 w-full px-4 py-2 rounded-full border border-stone-200 text-sm outline-none focus:border-[#2D5A27]"
              data-testid="visa-travel-month"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-black text-stone-500">Passport country</label>
            <input
              value={form.passport_country}
              onChange={(e) => setForm({ ...form, passport_country: e.target.value.toUpperCase().slice(0, 2) })}
              placeholder="IN"
              className="mt-2 w-full px-4 py-2 rounded-full border border-stone-200 text-sm outline-none focus:border-[#2D5A27]"
              data-testid="visa-passport"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-black text-stone-500">Notes (optional)</label>
            <input
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Purpose, family accompanying, etc."
              className="mt-2 w-full px-4 py-2 rounded-full border border-stone-200 text-sm outline-none focus:border-[#2D5A27]"
              data-testid="visa-notes"
            />
          </div>
        </div>

        <button
          onClick={submit}
          disabled={applying || !form.visa_type}
          className="mt-6 w-full md:w-auto bg-[#2D5A27] hover:bg-emerald-800 text-white font-black px-6 py-3 rounded-full inline-flex items-center gap-2 transition-colors disabled:opacity-50"
          data-testid="visa-apply-btn"
        >
          <ExternalLink size={16} /> Start application at the official portal <ArrowRight size={16} />
        </button>
        <button
          onClick={buildChecklist}
          disabled={checklistBusy || !form.visa_type}
          className="mt-3 md:mt-6 md:ml-3 w-full md:w-auto bg-white border-2 border-fuchsia-600 text-fuchsia-700 hover:bg-fuchsia-50 font-black px-6 py-3 rounded-full inline-flex items-center gap-2 transition-colors disabled:opacity-50"
          data-testid="visa-checklist-btn"
        >
          {checklistBusy ? <><Loader2 size={16} className="animate-spin" /> Generating…</> : <><Sparkles size={16} /> AI Document Checklist · ₹1</>}
        </button>
        <p className="text-[11px] text-stone-500 mt-3 max-w-lg">
          find2near links you directly to the country's official portal — we never handle your passport, biometrics or fees.
          We keep a record here so you can pick up where you left off.
        </p>
      </div>

      {checklist && (
        <div className="mt-6 rounded-3xl bg-gradient-to-br from-fuchsia-50 to-purple-50 border border-fuchsia-200 p-5 md:p-6" data-testid="visa-checklist-result">
          <div className="flex items-center gap-2 mb-3">
            <ClipboardCheck size={18} className="text-fuchsia-600" />
            <h3 className="heading text-lg font-black text-stone-900">Your AI-tailored checklist</h3>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-black text-fuchsia-700 mb-1">Documents</div>
              <ul className="space-y-1.5 text-sm">
                {(checklist.checklist?.documents || []).map((d, i) => (
                  <li key={i} className="flex items-start gap-2" data-testid={`visa-checklist-doc-${i}`}>
                    <input type="checkbox" className="mt-0.5" />
                    <div className="flex-1">
                      <span className="font-bold text-stone-900">{d.name}</span>
                      {d.required === false && <span className="ml-1 text-[10px] text-stone-500">(optional)</span>}
                      {d.why && <div className="text-xs text-stone-500">{d.why}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-black text-fuchsia-700 mb-1">Steps</div>
              <ol className="list-decimal list-inside text-sm space-y-1 text-stone-800">
                {(checklist.checklist?.steps || []).map((s, i) => (<li key={i}>{s}</li>))}
              </ol>
              {(checklist.checklist?.typical_pitfalls || []).length > 0 && (
                <div className="mt-3 rounded-2xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs">
                  <div className="font-black text-amber-800 uppercase tracking-widest text-[10px]">Pitfalls</div>
                  <ul className="mt-1 space-y-1 text-amber-900">
                    {checklist.checklist.typical_pitfalls.map((p, i) => (<li key={i}>• {p}</li>))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {applications.length > 0 && (
        <div className="mt-6 rounded-3xl bg-white border border-stone-200 p-5">
          <h3 className="heading text-lg font-black text-stone-900 mb-3">Your applications for {data.name}</h3>
          <ul className="space-y-2">
            {applications.map((a) => (
              <li key={a.application_id} className="flex flex-wrap items-center justify-between gap-2 text-sm bg-stone-50 rounded-xl px-3 py-2" data-testid={`visa-app-${a.application_id}`}>
                <div>
                  <div className="font-bold text-stone-900">{a.visa_type}</div>
                  <div className="text-xs text-stone-500">
                    Logged {new Date(a.created_at).toLocaleDateString()}
                    {a.travel_month ? ` · Travel ${a.travel_month}` : ""}
                  </div>
                </div>
                <a href={a.portal} target="_blank" rel="noopener noreferrer" className="text-[#2D5A27] font-bold text-xs inline-flex items-center gap-1 hover:underline">
                  Portal <ExternalLink size={12} />
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function Visa() {
  const { code } = useParams();
  const navigate = useNavigate();

  useSeo({
    title: code ? `${code} Visa · find2near` : "Visa Apply Portal · find2near",
    description: "Apply to 35+ country visas — official portal links, fees and processing times in one place.",
    canonical: code ? `/visa/${code}` : "/visa",
  });

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-8" data-testid="visa-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2">
          <Globe size={12} /> Visa Apply Portal
        </div>
        <h1 className="heading text-3xl md:text-4xl font-extrabold text-stone-900">
          Your passport, <span className="text-[#2D5A27]">every border.</span>
        </h1>
        <p className="text-stone-500 text-sm mt-1 max-w-xl">
          Search a country, pick a visa type, and start at its official portal. No hidden fees.
        </p>
      </div>

      {code
        ? <VisaDetail code={code.toUpperCase()} onBack={() => navigate("/visa")} />
        : <VisaList onOpen={(c) => navigate(`/visa/${c}`)} />}
    </div>
  );
}
